// Display-only component for the "📍 Use my location / 🔍 Search a
// place" bar. Pairs with the useLocationFilter hook — host page owns
// the filter state via the hook and passes it down here as a single
// prop. Lets us avoid 240 lines of identical JSX across multiple
// surfaces (PublicBrowsePage, MapViewPage, eventually others).

import React from 'react';
import { summarizePlace } from '../../utils/geo';

export default function LocationBar({ filter, h, t, hideSearchButton, hideLocationButton }) {
  const {
    userLocation, locationLabel, locationSource,
    geolocating, geoError, radiusKm, setRadiusKm,
    searchOpen, setSearchOpen, searchQ, setSearchQ,
    searchResults, searchLoading,
    recentPlaces,
    requestLocation, clearLocation, pickResult, clearRecent, dismissError,
  } = filter;

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Initial state — "Use my location" and/or "Search a place"
          buttons. Both can be hidden by props when the host page
          provides equivalent affordances elsewhere (e.g. map surfaces
          render 📍 + 🔍 buttons directly on the map, so the bar's
          buttons would be redundant). When BOTH are hidden the
          row vanishes entirely — the bar then only appears as the
          active chip + radius once a location has been picked.
          The remaining single button stretches to full width
          (flex: 1 + no sibling). */}
      {!userLocation && (!hideLocationButton || !hideSearchButton) && (
        <div style={{ display: "flex", gap: 6 }}>
          {!hideLocationButton && (
            <button
              onClick={requestLocation}
              disabled={geolocating}
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 12,
                border: `1px solid ${h.ac}30`,
                background: `${h.ac}08`,
                color: h.ac,
                fontSize: 12,
                fontWeight: 600,
                cursor: geolocating ? "wait" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                opacity: geolocating ? 0.7 : 1,
                minWidth: 0,
              }}
            >
              <span>{"📍"}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {geolocating ? (t.findingLocation || "Locating…") : (t.useMyLocation || "Use my location")}
              </span>
            </button>
          )}
          {!hideSearchButton && (
            <button
              onClick={() => setSearchOpen(true)}
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: 12,
                border: `1px solid ${h.bd}`,
                background: h.cd,
                color: h.tx,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                minWidth: 0,
              }}
            >
              <span>{"🔍"}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {t.searchAPlace || "Search a place"}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Search FULLSCREEN MODAL — opens when setSearchOpen(true) is
          called either via the bar's button (admin/no-map mode) or
          via the map's lens icon (browse / tenant map mode). Renders
          as a full-screen overlay so the input + suggestions + recent
          list have enough room to be tappable on mobile. */}
      {searchOpen && (
        <div
          onClick={() => { setSearchOpen(false); setSearchQ(""); }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 100001,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: 12,
            paddingTop: 40,
            animation: "fadeIn 0.15s ease-out",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: h.cd,
              borderRadius: 16,
              padding: 16,
              maxWidth: 480,
              width: "100%",
              maxHeight: "calc(100vh - 80px)",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 12px 48px rgba(0,0,0,0.3)",
              animation: "scaleIn 0.15s ease-out",
            }}
          >
            {/* Header — close button + title */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginBottom: 12, paddingBottom: 10,
              borderBottom: `1px solid ${h.bd}`,
            }}>
              <div style={{ color: h.tx, fontSize: 15, fontWeight: 700 }}>
                {t.searchAPlace || "Search a place"}
              </div>
              <button
                onClick={() => { setSearchOpen(false); setSearchQ(""); }}
                style={{
                  background: "none", border: "none",
                  color: h.sc, cursor: "pointer",
                  fontSize: 20, padding: "0 4px", lineHeight: 1,
                }}
                aria-label="Close"
              >{"×"}</button>
            </div>

            {/* Input */}
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "10px 12px",
              borderRadius: 10,
              background: h.bg,
              border: `1px solid ${h.bd}`,
              marginBottom: 12,
            }}>
              <span style={{ fontSize: 16, color: h.sc }}>{"🔍"}</span>
              <input
                autoFocus
                type="text"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                placeholder={t.searchPlaceholder || "Try a city or neighborhood"}
                style={{
                  flex: 1, minWidth: 0,
                  border: "none", outline: "none",
                  background: "transparent",
                  color: h.tx, fontSize: 14,
                  padding: "4px 0",
                }}
              />
              {searchQ && (
                <button
                  onClick={() => setSearchQ("")}
                  style={{
                    background: "none", border: "none",
                    color: h.sc, cursor: "pointer",
                    fontSize: 18, padding: 0, lineHeight: 1,
                  }}
                  aria-label="Clear"
                >{"×"}</button>
              )}
            </div>

            {/* Results / status — scrollable */}
            <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {searchLoading && (
                <div style={{ color: h.sc, fontSize: 12, padding: "10px 0", fontStyle: "italic", textAlign: "center" }}>
                  {t.searching || "Searching…"}
                </div>
              )}

              {!searchLoading && searchQ.trim().length >= 2 && searchResults.length === 0 && (
                <div style={{ color: h.sc, fontSize: 12, padding: "20px 0", fontStyle: "italic", textAlign: "center" }}>
                  {t.noPlacesFound || "No matching places. Try a broader name."}
                </div>
              )}

              {searchResults.length > 0 && (
                <div>
                  {searchResults.map(r => {
                    const { title, subtitle } = summarizePlace(r);
                    return (
                      <button
                        key={r.place_id}
                        onClick={() => pickResult(r)}
                        style={{
                          width: "100%", textAlign: "left",
                          padding: "10px 8px",
                          background: "transparent", border: "none",
                          borderBottom: `1px solid ${h.bd}40`,
                          cursor: "pointer",
                          display: "flex", alignItems: "flex-start", gap: 10,
                        }}
                      >
                        <span style={{ fontSize: 16, marginTop: 1 }}>{"📍"}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{title}</div>
                          {subtitle && <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{subtitle}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Recent — only when user hasn't typed yet */}
              {!searchLoading && searchQ.trim().length < 2 && recentPlaces.length > 0 && (
                <div>
                  <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "8px 4px",
                  }}>
                    <span style={{ color: h.sc, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                      {t.recentSearches || "Recent"}
                    </span>
                    <button
                      onClick={clearRecent}
                      style={{
                        background: "none", border: "none",
                        color: h.sc, cursor: "pointer",
                        fontSize: 11, fontWeight: 600, padding: 0,
                      }}
                    >
                      {t.clear || "Clear"}
                    </button>
                  </div>
                  {recentPlaces.map((r, i) => {
                    const { title, subtitle } = summarizePlace(r);
                    return (
                      <button
                        key={`recent_${r.place_id || i}`}
                        onClick={() => pickResult(r)}
                        style={{
                          width: "100%", textAlign: "left",
                          padding: "10px 8px",
                          background: "transparent", border: "none",
                          borderBottom: `1px solid ${h.bd}40`,
                          cursor: "pointer",
                          display: "flex", alignItems: "flex-start", gap: 10,
                        }}
                      >
                        <span style={{ fontSize: 16, marginTop: 1, opacity: 0.6 }}>{"🕘"}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{title}</div>
                          {subtitle && <div style={{ color: h.sc, fontSize: 11, marginTop: 2 }}>{subtitle}</div>}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {!searchLoading && searchQ.trim().length < 2 && recentPlaces.length === 0 && (
                <div style={{ color: h.sc, fontSize: 12, padding: "20px 8px", textAlign: "center", lineHeight: 1.5 }}>
                  {t.searchHint || "Type a city, neighbourhood, or landmark to find properties nearby."}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error MODAL — full-screen dim overlay + centered card. Opens
          when geoError is set (permission denied, timeout, GPS off,
          etc.) and dismisses the moment the user taps "Try again",
          "Search a place", "Close", or the backdrop. Replaces the
          previous always-visible inline banner which lingered after
          the user had moved on. */}
      {geoError && (() => {
        const isDenied = geoError.code === 'permission_denied';
        const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
        const message = typeof geoError === 'string' ? geoError : geoError.message;

        return (
          <div
            onClick={dismissError}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
              zIndex: 100000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 20,
              animation: "fadeIn 0.15s ease-out",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: h.cd,
                borderRadius: 16,
                padding: 20,
                maxWidth: 360,
                width: "100%",
                boxShadow: "0 12px 48px rgba(0,0,0,0.3)",
                animation: "scaleIn 0.15s ease-out",
              }}
            >
              {/* Title row with × close */}
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
                <div style={{
                  width: 40, height: 40,
                  borderRadius: 12,
                  background: `${h.wn}15`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 20, flexShrink: 0,
                }}>{isDenied ? "🚫" : "⚠️"}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: h.tx, fontWeight: 700, fontSize: 15, lineHeight: 1.3 }}>
                    {isDenied
                      ? (t.locationDenied || "Location access is blocked")
                      : (t.locationProblem || "Couldn't get your location")}
                  </div>
                  <div style={{ color: h.sc, fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>
                    {message}
                  </div>
                </div>
                <button
                  onClick={dismissError}
                  style={{
                    background: "none", border: "none",
                    color: h.sc, cursor: "pointer",
                    fontSize: 18, padding: 0,
                    lineHeight: 1, flexShrink: 0,
                  }}
                  aria-label="Close"
                >{"×"}</button>
              </div>

              {/* Recovery instructions (only for permission_denied) */}
              {isDenied && (
                <div style={{
                  background: h.bg,
                  borderRadius: 10,
                  padding: "10px 12px",
                  color: h.tx,
                  fontSize: 11,
                  lineHeight: 1.5,
                  marginBottom: 14,
                  border: `1px solid ${h.bd}`,
                }}>
                  <div style={{ color: h.sc, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                    {t.howToAllow || "How to allow"}
                  </div>
                  {isMobile
                    ? (t.locationDeniedMobile || "Open your device Settings → Apps → your browser → Permissions → Location → Allow. Then come back and tap Use my location again.")
                    : (t.locationDeniedDesktop || "Click the 🔒 / ⓘ icon in your address bar, find Location, change to Allow, then tap Use my location again.")}
                </div>
              )}

              {/* Recovery actions */}
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={() => { dismissError(); requestLocation(); }}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: `1px solid ${h.bd}`,
                    background: "transparent",
                    color: h.tx,
                    fontSize: 13, fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {t.tryAgain || "Try again"}
                </button>
                <button
                  onClick={() => { dismissError(); setSearchOpen(true); }}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "none",
                    background: h.ac,
                    color: "#fff",
                    fontSize: 13, fontWeight: 700,
                    cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                  }}
                >
                  <span>{"🔍"}</span>
                  <span>{t.searchInsteadCta || "Search a place"}</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Active state: chip + radius selector */}
      {userLocation && (
        <div style={{
          padding: "10px 12px",
          borderRadius: 12,
          background: `${h.gn}08`,
          border: `1px solid ${h.gn}25`,
        }}>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 8,
          }}>
            <span style={{ fontSize: 14 }}>{locationSource === 'search' ? "🔍" : "📍"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: h.gn, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {locationSource === 'search'
                  ? (t.searchingNear || "Near")
                  : (t.usingYourLocation || "Using your location")}
              </div>
              <div style={{ color: h.tx, fontSize: 12, fontWeight: 600, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {locationLabel || (t.sortedByNearest || "Sorted by nearest first")}
              </div>
            </div>
            <button
              onClick={clearLocation}
              style={{
                background: "none", border: "none",
                color: h.sc, cursor: "pointer",
                fontSize: 11, fontWeight: 600, padding: "4px 8px",
              }}
            >
              {t.clearLocation || "Clear"}
            </button>
          </div>
          {/* Radius chips */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {[
              { km: null, label: t.any || "Any" },
              { km: 1,    label: "1 km" },
              { km: 3,    label: "3 km" },
              { km: 5,    label: "5 km" },
              { km: 10,   label: "10 km" },
            ].map(opt => {
              const active = radiusKm === opt.km;
              return (
                <button
                  key={String(opt.km)}
                  onClick={() => setRadiusKm(opt.km)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 999,
                    border: `1px solid ${active ? h.ac : h.bd}`,
                    background: active ? `${h.ac}15` : "transparent",
                    color: active ? h.ac : h.sc,
                    fontSize: 11,
                    fontWeight: active ? 700 : 500,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
